import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import type { ReactNode } from 'react'
import type { ThemeMode } from '@bandos/shared'
import { ThemeContext, type ThemeContextType } from './ThemeContextDef'

const THEME_STORAGE_KEY = 'ideate-theme'

function getInitialTheme(): ThemeMode {
  // Check localStorage first for instant theme application
  const stored = localStorage.getItem(THEME_STORAGE_KEY)
  if (stored === 'light' || stored === 'dark') {
    return stored
  }
  return 'light'
}

interface ThemeProviderProps {
  children: ReactNode
  onThemeChange?: (theme: ThemeMode) => void
}

export function ThemeProvider({ children, onThemeChange }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemeMode>(getInitialTheme)
  // Track when we're in the middle of a user-initiated theme change
  // to ignore stale profile sync calls
  const userChangedThemeRef = useRef(false)
  const userChangeTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // Sync localStorage and apply theme when it changes
  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, theme)
    // Apply data-theme attribute for CSS variables
    document.body.setAttribute('data-theme', theme)
    // Update meta theme-color for mobile browser chrome
    const meta = document.querySelector('meta[name="theme-color"]')
    if (meta) {
      meta.setAttribute('content', theme === 'dark' ? '#141414' : '#ffffff')
    }
  }, [theme])

  const setTheme = useCallback(
    (newTheme: ThemeMode) => {
      // Mark that user initiated this change - ignore profile syncs for a bit
      userChangedThemeRef.current = true
      // Clear any existing timeout
      if (userChangeTimeoutRef.current) {
        clearTimeout(userChangeTimeoutRef.current)
      }
      // Reset flag after profile has time to update (500ms should be enough)
      userChangeTimeoutRef.current = setTimeout(() => {
        userChangedThemeRef.current = false
      }, 500)

      setThemeState(newTheme)
      onThemeChange?.(newTheme)
    },
    [onThemeChange]
  )

  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'light' ? 'dark' : 'light'
    setTheme(newTheme)
  }, [theme, setTheme])

  // Sync theme from profile when it loads (profile is source of truth on initial load)
  const syncFromProfile = useCallback(
    (profileTheme: ThemeMode | undefined) => {
      // Ignore if user just changed theme (avoids race with stale profile data)
      if (userChangedThemeRef.current) {
        return
      }
      if (profileTheme && profileTheme !== theme) {
        setThemeState(profileTheme)
        localStorage.setItem(THEME_STORAGE_KEY, profileTheme)
      }
    },
    [theme]
  )

  const value = useMemo<ThemeContextType>(
    () => ({ theme, setTheme, toggleTheme, syncFromProfile }),
    [theme, setTheme, toggleTheme, syncFromProfile]
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
